import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import LegalLayout from "@/components/site/LegalLayout";

const LAST_UPDATED = "December 2025";

const sections = [
  {
    id: "acceptance",
    title: "Acceptance of these terms",
    body: [
      "These Terms of Use (\"Terms\") govern your access to and use of the Rallie mobile app, the rallie.app website and any related services (together, the \"Service\"). By creating an account, downloading the app or otherwise using the Service, you agree to these Terms. If you do not agree, do not use the Service.",
      "We may update these Terms from time to time. The current version is always available at rallie.app/terms. Material changes will be communicated in-app or by email; continued use after an update means you accept the new version.",
    ],
  },
  {
    id: "eligibility",
    title: "Who can use Rallie",
    body: [
      "You must be at least 13 years old (or the minimum age in your country) to use Rallie. If you use the Service on behalf of a club or organisation, you confirm you have the authority to bind that entity to these Terms.",
      "You are responsible for keeping your account credentials secure. Everything that happens under your account is your responsibility.",
    ],
  },
  {
    id: "license",
    title: "Your licence to use Rallie",
    body: [
      "Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable licence to install and use Rallie for personal, non-commercial purposes on devices you own or control.",
      "You may not: reverse engineer, decompile or attempt to extract source code from the app; sublicense, rent, sell or lease access to the Service; use the Service to build a competing product; or remove any copyright, trademark or attribution notices.",
    ],
  },
  {
    id: "user-content",
    title: "Your content",
    body: [
      "You retain ownership of the content you create in Rallie — including tournaments, match records, comments and profile info (\"User Content\"). By posting User Content, you grant Rallie a worldwide, royalty-free licence to host, store, display and process it strictly to operate and improve the Service.",
      "You are responsible for your User Content. You confirm you have the right to share it and that it does not violate any law or third-party right.",
    ],
  },
  {
    id: "acceptable-use",
    title: "Acceptable use",
    body: [
      "You agree not to:",
      {
        type: "list",
        items: [
          "Use the Service for anything unlawful, deceptive, harmful or infringing.",
          "Harass, threaten, impersonate or dox other players.",
          "Upload malware, spam or automated scripts that put load on our systems.",
          "Manipulate scores, rankings or stats to gain an unfair advantage.",
          "Attempt to access accounts, data or systems you are not authorised to.",
        ],
      },
      "We may investigate any suspected violation and, at our discretion, suspend or terminate accounts.",
    ],
  },
  {
    id: "accounts",
    title: "Accounts, suspension & deletion",
    body: [
      "You can delete your Rallie account at any time from Profile → Settings → Delete Account inside the app, or by following the instructions at rallie.app/delete-account.",
      "We may suspend or terminate your access if you materially breach these Terms, if we are required to by law, or if we discontinue the Service. Where possible we will give you reasonable notice.",
    ],
  },
  {
    id: "third-parties",
    title: "Third-party services",
    body: [
      "Rallie may link to or integrate with third-party services (for example the App Store, Google Play or authentication providers). We are not responsible for those services and your use of them is governed by their own terms and privacy policies.",
    ],
  },
  {
    id: "intellectual-property",
    title: "Our intellectual property",
    body: [
      "The Service — including the Rallie name, logo, look and feel, code, designs and content we provide — belongs to us or our licensors and is protected by intellectual property laws. Nothing in these Terms grants you any right in our marks or content beyond the limited licence above.",
    ],
  },
  {
    id: "disclaimers",
    title: "Disclaimers",
    body: [
      "The Service is provided \"as is\" and \"as available\" without warranties of any kind, whether express or implied, including merchantability, fitness for a particular purpose and non-infringement.",
      "We do not warrant that the Service will be uninterrupted, error-free, secure, or that scores, stats and rankings will be perfectly accurate at all times.",
    ],
  },
  {
    id: "liability",
    title: "Limitation of liability",
    body: [
      "To the maximum extent permitted by law, Rallie will not be liable for any indirect, incidental, special, consequential or punitive damages, or any loss of profits, revenue, data or goodwill, arising from your use of the Service.",
      "Our total liability for any claim arising out of or relating to the Service is limited to the greater of (a) the amount you paid us in the 12 months before the claim (which, for a free product, is zero) or (b) €50.",
    ],
  },
  {
    id: "indemnity",
    title: "Indemnity",
    body: [
      "You agree to defend, indemnify and hold harmless Rallie and its team from any claim, demand or expense (including reasonable legal fees) arising out of your User Content, your use of the Service, or your breach of these Terms.",
    ],
  },
  {
    id: "governing-law",
    title: "Governing law & disputes",
    body: [
      "These Terms are governed by the laws of your country of residence to the extent required by consumer protection rules, and otherwise by the laws of Italy, without regard to conflict-of-laws principles.",
      "Any dispute arising out of these Terms will be resolved by the competent courts of Milan, Italy, unless mandatory law requires otherwise.",
      {
        type: "note",
        text: "Nothing in these Terms limits any rights you have as a consumer under mandatory local law.",
      },
    ],
  },
  {
    id: "contact",
    title: "Contact us",
    body: [
      "Questions about these Terms? Email hello@rallie.app or open a ticket at rallie.app/support.",
    ],
  },
];

export default function Terms() {
  return (
    <div className="bg-black text-white min-h-screen">
      <Nav />

      <div className="pt-28 max-w-4xl mx-auto px-6 md:px-10">
        <Link
          to="/"
          data-testid="terms-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-[#D4F34A] transition"
        >
          <ArrowLeft size={14} />
          Back to Rallie
        </Link>
      </div>

      <LegalLayout
        testId="terms-page"
        eyebrow="Legal"
        title={
          <>
            Terms
            <br />
            of use.
          </>
        }
        lastUpdated={LAST_UPDATED}
        intro="These Terms set out the rules for using Rallie. They're written to be readable, but they are still a legal agreement between you and us — please read them carefully."
        sections={sections}
      />

      <Footer />
    </div>
  );
}
